import "./lib/sentry";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  QueryCache,
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Toaster } from "@sportscanner/ui";
import "@/i18n/index";
import "@/index.css";
import App from "./App";
import ErrorFallback from "./components/error-fallback";
import RateLimitGate from "./components/rate-limit-gate";

function reportToSentry(err: unknown) {
  const status = (err as { status?: number } | null | undefined)?.status;
  // Operational 4xx errors are expected user-input failures, not bugs —
  // skip them. Anything else (5xx, network failure, unexpected throw) is
  // worth capturing.
  if (typeof status === "number" && status < 500) return;
  Sentry.captureException(err);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
  queryCache: new QueryCache({ onError: reportToSentry }),
  mutationCache: new MutationCache({ onError: reportToSentry }),
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorFallback error={error} resetError={resetError} />
      )}
    >
      <QueryClientProvider client={queryClient}>
        <App />
        <Toaster position="top-right" />
        <RateLimitGate />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
